import React, { useRef, useState } from "react";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../../services/api.js";
import { Toast } from "../../shared/Toast.js";
import { SlButton, SlDrawer } from "@shoelace-style/shoelace/dist/react";
import { useForm } from "react-hook-form";
import PropsalPdf from "./PropsalPdf.js";
import ScrollContainer from "react-indiana-drag-scroll";

function SalesPriceCalculator() {
  const [price, setPrice] = useState({ priceObj: {} });
  const [constituencyOptions, setConstituencyOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [discounts, setDiscounts] = useState({}); // Track discounts for each position
  const [selected, setSelected] = useState([]);
  const [showProposal, setShowProposal] = useState(false);
  const [adType, setAdType] = useState(null);
  const form1 = useForm({ mode: "onChange" });

  const watchBrand = form1.watch("brand_name", "");
  const watchAddress = form1.watch("address", "");
  const watchCompany = form1.watch("company", "");
  const watchLocation = form1.watch("location", "");
  const watchName = form1.watch("name", "");

  const positionOptions = [
    { label: "SOV", value: "SOV" },
    // {label:'0', value:'0'},
    { label: "1", value: "1" },
    { label: "3", value: "3" },
    { label: "5", value: "5" },
    { label: "9", value: "9" },
    { label: "12", value: "12" },
    { label: "15", value: "15" },
    // {label:'19', value:'19'},
    // {label:'23', value:'23'},
    // {label:'27', value:'27'},
  ];
  const getPositionArray = (selectedPosition) => {
    const selectedIndex = positionOptions.findIndex(
      (item) => item.value === selectedPosition
    );
    if (selectedIndex === -1) return []; // Return empty array if position is not found

    const previous =
      selectedIndex > 0 ? positionOptions[selectedIndex - 1] : null;
    const next =
      selectedIndex < positionOptions.length - 1
        ? positionOptions[selectedIndex + 1]
        : null;
    // Return an array with previous, selected, and next positions
    return [
      previous?.value, // Previous position (if any)
      positionOptions[selectedIndex].value, // Current selected position
      next?.value, // Next position (if any)
    ].filter((item) => item !== null); // Filter out null values if there is no previous or next
  };
  function handleRequestClose(event) {
    if (event.detail.source === "overlay") {
      event.preventDefault();
    }
  }
  const formik = useFormik({
    initialValues: {
      lang_ids: [],
      state_ids: [],
      district_ids: [],
      mandal_ids: [],
      village_ids: [],
      constituency_ids: [],
      creative_type: "",
      position: "",
    },
    validationSchema: Yup.object({
      lang_ids: Yup.array()
        .required("Language is required")
        .min(1, "Please select atleast one language"),
      state_ids: Yup.array()
        .required("State is required")
        .min(1, "Please select atleast one state"),
      district_ids: Yup.array(),
      mandal_ids: Yup.array(),
      village_ids: Yup.array(),
      constituency_ids: Yup.array(),
      creative_type: Yup.string()
        .required("Creative Type is required")
        .min(1, "Please select creative type"),
      position: Yup.string()
        .required("Position is required")
        .min(1, "Please select atleast one position"),
    }),
    onSubmit: async (data) => {
      setAdType(data.creative_type);
      try {
        const res = await api({
          url: `/saleslocation/getPrice`,
          method: "POST",
          responseType: "json",
          data: {
            state_ids: data.state_ids,
            district_ids: data.district_ids,
            position: getPositionArray(data.position),
            lang_ids: data.lang_ids,
            constituency_ids: data.constituency_ids,
            creative_type: data.creative_type,
          },
        });
        if (res.code === 200) {
          if (res.data === -1) {
            Toast("error", "Price not available for selected target");
          } else {
            setPrice(res.data);
          }
        }
      } catch (e) {
        console.error(e);
      }
    },
  });

  const lang_idsOnchange = async (selected) => {
    const langs = selected ? selected.map((item) => item.value) : [];
    try {
      let res = await api({
        url: "/saleslocation/states",
        method: "POST",
        responseType: "json",
        data: {
          language: langs,
        },
      });
      let states = [];
      for await (let item of res.data) {
        const temp = { value: item, label: `${item.value}` };
        states.push(temp);
      }
      setStateOptions(states);
      formik.setFieldValue(
        "state_ids",
        formik.values.state_ids.filter(
          (option) => langs.indexOf(option.language) >= 0
        )
      );
      formik.setFieldValue(
        "district_ids",
        formik.values.district_ids.filter(
          (option) => langs.indexOf(option.language) >= 0
        )
      );
      formik.setFieldValue(
        "constituency_ids",
        formik.values.constituency_ids.filter(
          (option) => langs.indexOf(option.language) >= 0
        )
      );
    } catch (error) {
      console.error(error);
    }
  };
  const onchange = (selected, field) => {
    // Set the Formik field value based on the selected items
    formik.setFieldValue(
      field,
      selected ? selected.map((item) => item.value) : []
    );

    // Handle the "state_ids" change
    if (field === "state_ids") {
      // Get the previous selected districts' states
      const districtStates = new Set(
        formik.values.district_ids.map((district) => district.state)
      );
      // Get the newly selected states
      const selectedStates = new Set(selected.map((item) => item.value.value));
      // If the selected states differ from the districts' states, show the toast
      if (districtStates.size === 0) {
        return;
      }
      if (![...selectedStates].every((state) => districtStates.has(state))) {
        Toast("warning", "Please check your selection: Price may increase.");
      }
    }

    // Handle the "district_ids" change
    if (field === "district_ids") {
      // Get the selected districts
      const selectedDistricts = new Set(
        selected.map((item) => item.value.value)
      );
      // Get the previously selected constituencies' districts
      const constituencyDistricts = new Set(
        formik.values.constituency_ids.map(
          (constituency) => constituency.district
        )
      );

      if (constituencyDistricts.size === 0) {
        return;
      }
      // If the selected constituencies don't match the selected districts, show the toast
      if (
        ![...selectedDistricts].every((district) =>
          constituencyDistricts.has(district)
        )
      ) {
        Toast("warning", "Please check your selection: Price may increase.");
      }
    }
  };

  const state_idsOnchange = async (selected, langs) => {
    const states = selected
      ? selected.map((item) => {
          if (item.value) return item.value?.value;
        })
      : [];
    // const langs = formik.values.lang_ids
    try {
      let res = await api({
        url: "/saleslocation/districts",
        method: "POST",
        responseType: "json",
        data: { states, language: langs },
      });
      let districts = [];
      for await (let item of res.data) {
        const temp = { value: item, label: `${item.value}` };
        districts.push(temp);
      }
      setDistrictOptions(districts);
      let temp = [];
      await formik.setFieldValue(
        "district_ids",
        formik.values.district_ids.filter((option) => {
          if (states.indexOf(option.state) >= 0) {
            temp.push(option.label);
            return true;
          }
        })
      );
      formik.setFieldValue(
        "constituency_ids",
        formik.values.constituency_ids.filter(
          (option) => temp.indexOf(option.district) >= 0
        )
      );
    } catch (error) {
      console.error(error);
    }
  };
  const district_idsOnchange = async (selected, langs, states) => {
    const districts = selected
      ? selected.map((item) => {
          if (item.value) return item.value?.value;
        })
      : [];
    states = states.map((item) => {
      if (item.value) return item.value;
    });
    try {
      let consties = await api({
        url: `/saleslocation/constituencies`,
        method: "POST",
        responseType: "json",
        data: { districts, language: langs, states },
      });
      let constituents = [];
      for await (let item of consties.data) {
        const temp = { value: item, label: `${item.label}` };
        constituents.push(temp);
      }
      setConstituencyOptions(constituents);
      formik.setFieldValue(
        "constituency_ids",
        formik.values.constituency_ids.filter(
          (option) => districts.indexOf(option.district) >= 0
        )
      );
    } catch (error) {
      console.error(error);
    }
  };
  const getvalues = (Array, field) => {
    if (Array.length > 0) {
      switch (field) {
        case "lang_ids":
          return Array.filter(
            (option) => formik.values.lang_ids.indexOf(option.value) >= 0
          );
        case "state_ids":
          let fun = [];
          formik.values.state_ids.map((option) => {
            fun.push(option.value);
            return null;
          });
          return Array.filter((option) => fun.indexOf(option.label) >= 0);
        case "district_ids":
          const res1 = [];
          formik.values.district_ids.map((option) => {
            res1.push(option.value);
            return null;
          });
          return Array.filter((option) => res1.indexOf(option.label) >= 0);
        case "constituency_ids":
          const res4 = [];
          formik.values.constituency_ids.map((option) => {
            res4.push(option.value);
            return null;
          });

          return Array.filter((option) => res4.indexOf(option.label) >= 0);
        case "position":
          return Array.filter(
            (option) => formik.values.position?.indexOf(option.value) >= 0
          );
        // case "pincodes":
        // return Array.filter((option) => formik.values.pincodes.indexOf(option.value) >= 0)

        default:
          return [];
      }
    } else {
      return [];
    }
  };

  const handleCheckboxChange = (item) => {
    const updatedSelected = selected.includes(item)
      ? selected.filter((selectedItem) => selectedItem !== item)
      : [...selected, item];
    setSelected(updatedSelected);
  };

  // Handle discount input change
  const handleDiscountChange = (position, discountValue) => {
    const discount = parseFloat(discountValue) || 0;
    const originalPrice = price["priceObj"][position] || 0;

    // Calculate discounted price
    const discountedPrice = originalPrice - (originalPrice * discount) / 100;

    // Update state with new discount and discounted price
    setDiscounts({
      ...discounts,
      [position]: {
        discount: discountValue,
        discountedPrice: discountedPrice < 0 ? 0 : discountedPrice.toFixed(2), // Prevent negative values
      },
    });
  };

  const downloadDocumnet = () => {
    return;
  };

  function generateLocationString(data, from = "table") {
    const { state_ids, district_ids, constituency_ids } = data;
    let locationStrings = [];

    // If districts are present, include State and District
    if (district_ids.length > 0) {
      district_ids.forEach((district) => {
        locationStrings.push(`${district.state}(S), ${district.label}(D).`);
      });
    }

    // If constituencies are present, include State, District, and Constituency
    if (constituency_ids.length > 0) {
      constituency_ids.forEach((constituency) => {
        locationStrings.push(
          `${constituency.state}(S), ${constituency.district}(D), ${constituency.label}(C).`
        );
      });
    }

    // If no districts or constituencies but only state is selected
    if (
      state_ids.length > 0 &&
      district_ids.length === 0 &&
      constituency_ids.length === 0
    ) {
      state_ids.forEach((state) => {
        locationStrings.push(`${state.label}(S)`);
      });
    }

    // Join all strings by a newline
    if (from === "table") {
      return locationStrings.join("<br />");
    } else if (from === "pdf") {
      return locationStrings.join("\n");
    }
  }

  return (
    <>
      <div className="mx-auto">
        <form className="contents">
          <div className="mb-3">
            <label htmlFor="" className="form-label">
              Language
            </label>
            <span className="text-red-600">*</span>
            <Select
              // class="form-select"
              aria-label="Default select example"
              name="lang_ids"
              placeholder="select language"
              onBlur={formik.handleBlur}
              value={getvalues(
                [{ label: "Telugu", value: "Telugu" }],
                "lang_ids"
              )}
              onChange={(selected) => {
                onchange(selected, "lang_ids");
                lang_idsOnchange(selected);
              }}
              options={[{ label: "Telugu", value: "Telugu" }]}
              isMulti
            />
            {formik.errors.lang_ids && formik.touched.lang_ids ? (
              <div className="text-red-600">{formik.errors.lang_ids}</div>
            ) : null}
          </div>
          <div>
            <div className="mb-3">
              <label htmlFor="" className="form-label">
                State
              </label>{" "}
              <span className="text-red-600">*</span>
              <Select
                // class="form-select"
                aria-label="Default select example"
                name="states_ids"
                placeholder="Select States"
                onBlur={formik.handleBlur}
                value={getvalues(stateOptions, "state_ids")}
                onChange={(selected) => {
                  onchange(selected, "state_ids");
                  state_idsOnchange(selected, formik.values.lang_ids);
                }}
                options={stateOptions}
                isMulti
              />
              {formik.errors.state_ids && formik.touched.state_ids ? (
                <div className="text-red-600">{formik.errors.state_ids}</div>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="" className="form-label">
                Districts
              </label>
              <Select
                // class="form-select"
                aria-label="Default select example"
                name="district_ids"
                placeholder="Select Districts"
                onBlur={formik.handleBlur}
                value={getvalues(districtOptions, "district_ids")}
                onChange={(selected) => {
                  onchange(selected, "district_ids");
                  district_idsOnchange(
                    selected,
                    formik.values.lang_ids,
                    formik.values.state_ids
                  );
                }}
                options={districtOptions}
                isMulti
              />
            </div>

            <div className="mb-3">
              <label htmlFor="" className="form-label">
                Constituency
              </label>
              <Select
                // class="form-select"
                aria-label="Default select example"
                name="constituency_ids"
                placeholder="Select Constituency"
                onBlur={formik.handleBlur}
                value={getvalues(constituencyOptions, "constituency_ids")}
                options={constituencyOptions}
                onChange={(selected) => {
                  onchange(selected, "constituency_ids");
                }}
                isMulti
              />
            </div>
          </div>
          <div className="mb-3">
            <label htmlFor="" className="form-label">
              Position
            </label>
            <span className="text-red-600">*</span>
            <select
              name="position"
              placeholder="Select Position"
              onBlur={formik.handleBlur}
              className="form-select"
              onChange={(e) => {
                formik.handleChange(e);
              }}
            >
              <option value="">Select Position</option>
              {positionOptions.map((position) => (
                <option key={position.value} value={position.value}>
                  {position.label}
                </option>
              ))}
            </select>

            {formik.errors.position && formik.touched.position ? (
              <div className="text-red-600">{formik.errors.position}</div>
            ) : null}
          </div>
          <div className="mb-3">
            <label htmlFor="" className="form-label">
              Creative Type
            </label>
            <span className="text-red-600">*</span>
            <select
              onBlur={formik.handleBlur}
              onChange={(e) => {
                formik.handleChange(e);
              }}
              value={formik.values.creative_type}
              name="creative_type"
              className="form-select"
              aria-label="Default select example"
            >
              <option value="">select creative type</option>
              {
                <>
                  <option value="Full Page">Full Image</option>
                  <option value="Full Page Video">Full Page Video</option>
                  <option value="Promoted Story">Promoted Story</option>
                </>
              }
            </select>
            {formik.touched.creative_type && formik.errors.creative_type ? (
              <div className="text-red-600">{formik.errors.creative_type}</div>
            ) : null}
          </div>
          <div className="mb-3">
            <SlButton
              variant="primary"
              className="w-full"
              onClick={() => formik.handleSubmit()}
            >
              {" "}
              Get Price
            </SlButton>
          </div>
        </form>
        <ScrollContainer>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th></th>
                <th>Position</th>
                <th>Location</th>
                <th>CardList Price</th>
                <th>Apply Discount</th>
                <th>
                  Discounted Price{"   "}
                  {selected?.length > 0 ? (
                    <SlButton
                      variant="primary"
                      size="small"
                      onClick={() => {
                        setShowProposal(true);
                      }}
                    >
                      Generate Proposal
                    </SlButton>
                  ) : (
                    ""
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(price?.priceObj).map((item, index) => {
                // Check if the price is 0 and skip rendering the row if it is
                if (price["priceObj"][item] === 0) {
                  return null;
                }

                return (
                  <tr key={item}>
                    <td>
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          value=""
                          id={`${item}`}
                          onChange={() => handleCheckboxChange(item)}
                        />
                      </div>
                    </td>
                    <td>{item}</td>
                    <td
                      dangerouslySetInnerHTML={{
                        __html: generateLocationString(price),
                      }}
                    />
                    <td>{price["priceObj"][item]}</td>
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        value={discounts[item]?.discount || ""}
                        onChange={(e) =>
                          handleDiscountChange(item, e.target.value)
                        }
                        placeholder="Enter discount %"
                      />
                    </td>
                    <td>{discounts[item]?.discountedPrice || price[item]}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollContainer>
      </div>
      {showProposal && (
        <SlDrawer
          open={showProposal}
          onSlAfterHide={() => {
            setShowProposal(false);
            form1.reset();
          }}
          label="Generate Propsal"
          onSlRequestClose={handleRequestClose}
          style={{ "--size": `${window.innerWidth <= 767 ? "100vw" : "80vw"}` }}
        >
          <div className="d-flex gap-3 md:lex-wrap-reverse">
            <div className="lg:w-8/12 md:w-full w-full">
              <PropsalPdf
                brand_name={watchBrand}
                company={watchCompany}
                address={watchAddress}
                location={watchLocation}
                price={price}
                discounts={discounts}
                generateLocationString={generateLocationString}
                adType={adType}
                selected={selected}
                name={watchName}
              />
            </div>
            <form
              onSubmit={form1.handleSubmit(downloadDocumnet)}
              className="lg:w-4/12 md:w-full w-full"
            >
              <div className="mb-3">
                <label htmlFor="" className="form-label">
                  Company
                </label>
                <span className="text-red-600">*</span>
                <input
                  type="text"
                  className="form-control"
                  {...form1.register("company", { required: true })}
                />
                {form1.formState?.errors?.company && (
                  <p className="text-red-500">This is a required Field.</p>
                )}
              </div>
              <div className="mb-3">
                <label htmlFor="" className="form-label">
                  Brand Name
                </label>
                <span className="text-red-600">*</span>
                <input
                  type="text"
                  className="form-control"
                  {...form1.register("brand_name", { required: true })}
                />
                {form1.formState?.errors?.brand_name && (
                  <p className="text-red-500">This is a required Field.</p>
                )}
              </div>
              <div className="mb-3">
                <label htmlFor="" className="form-label">
                  Location
                </label>
                <span className="text-red-600">*</span>
                <input
                  type="text"
                  className="form-control"
                  {...form1.register("location", { required: true })}
                />
                {form1.formState?.errors?.location && (
                  <p className="text-red-500">This is a required Field.</p>
                )}
              </div>
              <div className="mb-3">
                <label htmlFor="" className="form-label">
                  Name
                </label>
                <span className="text-red-600">*</span>
                <input
                  type="text"
                  className="form-control"
                  {...form1.register("name", { required: true })}
                />
                {form1.formState?.errors?.name && (
                  <p className="text-red-500">This is a required Field.</p>
                )}
              </div>
              <div className="mb-3">
                <label htmlFor="" className="form-label">
                  Address
                </label>
                <input
                  type="textbox"
                  className="form-control"
                  {...form1.register("address", { required: false })}
                />
              </div>
              <div className="mb-3">
                <SlButton variant="primary" className="w-full" type="submit">
                  Generate Proposal
                </SlButton>
              </div>
            </form>
          </div>
        </SlDrawer>
      )}
    </>
  );
}

export default SalesPriceCalculator;
